import { useCallback, useEffect, useState } from "react";
import { RefreshCw, HardDrive, Cloud, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Badge, Empty, Field, PageHeader } from "@/components/common";
import { api, formatApiError } from "@/lib/api";
import { BACKUP_STATE, fmtBytes } from "@/lib/constants";

const dt = (iso) => (iso ? new Date(iso).toLocaleString("fr-FR", { timeZone: "Europe/Paris", dateStyle: "short", timeStyle: "short" }) : "—");
const dur = (r) => (r?.started_at && r?.ended_at ? `${Math.max(0, Math.round((new Date(r.ended_at) - new Date(r.started_at)) / 60000))} min` : "—");
const yes = (ok, checked = true) => (!checked ? "Non vérifié" : ok ? "OK" : "Échec");

const Item = ({ k, v, testid }) => (
  <div><p className="text-xs uppercase tracking-wider text-slate-500">{k}</p><p className="text-sm text-slate-200" data-testid={testid}>{v}</p></div>
);

const LastRun = ({ r }) => {
  if (!r) return <Empty testid="backup-no-run">Aucune sauvegarde reçue pour l'instant. Installez le script d'appel (deploy/README-SAUVEGARDES.md).</Empty>;
  const l = r.local || {}, o = r.onedrive || {}, d = r.disk || {};
  return (
    <div className="grid gap-4 md:grid-cols-2">
      <section className="space-y-3 rounded-xl border border-cyan-400/15 bg-[#122549]/60 p-4" data-testid="backup-local-card">
        <h2 className="flex items-center gap-2 font-display text-base font-semibold text-white"><HardDrive className="h-4 w-4 text-cyan-300" />Sauvegarde locale (VPS)</h2>
        <div className="grid grid-cols-2 gap-3">
          <Item k="Résultat" v={yes(l.ok)} testid="backup-local-result" /><Item k="Archive" v={l.file || "—"} />
          <Item k="Taille" v={fmtBytes(l.size)} testid="backup-local-size" /><Item k="Contrôle gzip" v={l.gzip_ok ? "Lisible" : "Échec"} />
          <Item k="Archives conservées" v={l.count ?? "—"} /><Item k="Espace disque libre" v={`${fmtBytes(d.free_bytes)} / ${fmtBytes(d.total_bytes)}`} testid="backup-disk-free" />
          <Item k="Fichiers de l'application" v={r.files?.dir ? `${r.files.count ?? 0} fichier(s) · ${fmtBytes(r.files.size)}` : "Dans MongoDB (signatures, bons) : inclus dans l'archive"} />
          <Item k="Code de sortie sauvegarde.sh" v={r.exit_code ?? "—"} />
        </div>
      </section>
      <section className="space-y-3 rounded-xl border border-cyan-400/15 bg-[#122549]/60 p-4" data-testid="backup-onedrive-card">
        <h2 className="flex items-center gap-2 font-display text-base font-semibold text-white"><Cloud className="h-4 w-4 text-cyan-300" />Transfert OneDrive</h2>
        <div className="grid grid-cols-2 gap-3">
          <Item k="Résultat" v={yes(o.ok, o.checked)} testid="backup-onedrive-result" /><Item k="Taille sur OneDrive" v={fmtBytes(o.size)} />
          <Item k="Dossier" v={o.remote || "—"} /><Item k="Contrôlé le" v={dt(r.ended_at)} />
        </div>
        {o.error && <p className="text-xs text-red-300">{o.error}</p>}
      </section>
    </div>
  );
};

const SettingsBox = ({ cfg, onSave }) => {
  const [f, setF] = useState(cfg);
  useEffect(() => { setF(cfg); }, [cfg]);
  return (
    <section className="space-y-3 rounded-xl border border-cyan-400/15 bg-[#122549]/60 p-4" data-testid="backup-settings">
      <h2 className="font-display text-base font-semibold text-white">Contrôle et alertes</h2>
      <label className="flex items-center gap-3 text-sm text-slate-200"><Switch checked={f.enabled} onCheckedChange={(v) => setF({ ...f, enabled: v })} data-testid="backup-enabled-switch" />Détecter une sauvegarde non exécutée</label>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Field label="Heure prévue"><Input type="time" value={f.time} onChange={(e) => setF({ ...f, time: e.target.value })} data-testid="backup-time-input" /></Field>
        <Field label="Tolérance (min)"><Input type="number" min={10} value={f.tolerance_minutes} onChange={(e) => setF({ ...f, tolerance_minutes: Number(e.target.value) })} data-testid="backup-tolerance-input" /></Field>
        <Field label="Durée max. (min)"><Input type="number" min={30} value={f.max_duration_minutes} onChange={(e) => setF({ ...f, max_duration_minutes: Number(e.target.value) })} data-testid="backup-maxduration-input" /></Field>
        <Field label="E-mail d'alerte"><Input type="email" value={f.alert_email} onChange={(e) => setF({ ...f, alert_email: e.target.value })} data-testid="backup-alert-email-input" /></Field>
      </div>
      <Button variant="outline" onClick={() => onSave(f)} data-testid="backup-settings-save-button">Enregistrer</Button>
    </section>
  );
};

const History = ({ runs }) => (
  <section className="rounded-xl border border-cyan-400/15 bg-[#122549]/60 p-4" data-testid="backup-history">
    <h2 className="mb-2 font-display text-base font-semibold text-white">Historique</h2>
    {!runs.length && <p className="text-sm text-slate-400">Aucune exécution enregistrée.</p>}
    <ul className="space-y-1.5">
      {runs.map((r) => (
        <li key={r.run_id} className="flex flex-wrap items-center gap-2 text-sm" data-testid={`backup-run-${r.run_id}`}>
          <span className="font-mono-dt w-32 text-xs text-slate-400">{dt(r.started_at)}</span>
          <Badge value={r.result} map={BACKUP_STATE} />
          {r.test && <span className="rounded border border-white/15 px-1.5 text-xs text-slate-400">Test</span>}
          <span className="text-xs text-slate-300">Locale {yes(r.local?.ok)} · OneDrive {yes(r.onedrive?.ok, r.onedrive?.checked)} · {fmtBytes(r.local?.size)} · {dur(r)}</span>
        </li>
      ))}
    </ul>
  </section>
);

export default function Backups() {
  const [s, setS] = useState(null);
  const run = useCallback((p) => p.then((r) => setS(r.data)).catch((e) => toast.error(formatApiError(e))), []);
  useEffect(() => { run(api.get("/backups/status")); }, [run]);
  if (!s) return <p className="text-slate-400">Chargement…</p>;
  return (
    <>
      <PageHeader title="Sauvegardes" subtitle="Supervision de la sauvegarde quotidienne MongoDB et de sa copie OneDrive (lecture seule)">
        <Button variant="outline" className="h-11" onClick={() => run(api.post("/backups/check")).then(() => toast.success("État actualisé"))} data-testid="backup-refresh-button"><RefreshCw className="mr-2 h-4 w-4" />Actualiser l'état</Button>
        <Button variant="outline" className="h-11" onClick={() => api.post("/backups/test-alert").then((r) => { const s = r.data.status; if (s === "sent") toast.success(`E-mail de test envoyé à ${r.data.to}`); else if (s === "skipped") toast.warning("Aucune adresse d'alerte renseignée"); else toast.error(`Échec de l'envoi : ${r.data.error}`); run(api.get("/backups/status")); }).catch((e) => toast.error(formatApiError(e)))} data-testid="backup-test-alert-button">Tester l'e-mail d'alerte</Button>
      </PageHeader>
      <div className="flex flex-wrap items-center gap-4 rounded-xl border border-cyan-400/20 bg-[#122549]/70 p-4" data-testid="backup-state-card">
        <Badge value={s.state} map={BACKUP_STATE} testid="backup-state-badge" />
        <Item k="Dernière sauvegarde" v={dt(s.last?.started_at)} testid="backup-last-date" />
        <Item k="Durée" v={dur(s.last)} />
        <Item k="Dernière réussite" v={dt(s.last_success?.started_at)} />
        <Item k="Prochaine prévue" v={dt(s.next_planned)} testid="backup-next-planned" />
        <Item k="Contrôlé" v={dt(s.checked_at)} />
      </div>
      {s.problems?.length > 0 && (
        <div className="space-y-1 rounded-xl border border-amber-400/40 bg-amber-500/10 p-3 text-sm text-amber-100" data-testid="backup-problems">
          {s.problems.map((p) => <p key={p} className="flex gap-2"><AlertTriangle className="h-4 w-4 shrink-0" />{p}</p>)}
        </div>
      )}
      <LastRun r={s.last} />
      <History runs={s.runs} />
      <section className="rounded-xl border border-cyan-400/15 bg-[#122549]/60 p-4" data-testid="backup-alerts">
        <h2 className="mb-2 font-display text-base font-semibold text-white">Alertes envoyées</h2>
        {!s.alerts.length && <p className="text-sm text-slate-400">Aucune alerte.</p>}
        {s.alerts.map((a) => <p key={a.key} className="text-sm text-slate-300"><span className="font-mono-dt text-xs text-slate-500">{dt(a.at)}</span> {a.subject} · <span className="text-xs text-slate-400">{a.status === "sent" ? `envoyée à ${a.to}` : a.status === "skipped" ? "non envoyée (pas d'adresse)" : a.status === "error" ? "échec d'envoi" : "en cours"}{a.resolved ? " · résolue" : ""}</span></p>)}
      </section>
      <SettingsBox cfg={s.config} onSave={(f) => run(api.put("/backups/settings", f)).then(() => toast.success("Réglages enregistrés"))} />
    </>
  );
}
